// Resolves the auth token for the session app.
//
// Sources, in order:
//   1. localStorage.__ibis_token__ ({"token": "..."}), written by the legacy dashboard
//      on the same origin or by the partner ?ticket= login.
//   2. cookie __ibis_token__ (JSON {"token": "..."}, possibly URL-encoded), written by the
//      new dashboard (ibis-dashboard) on domain .ibisvision.co.uk.
//   3. cookie ibisvision_token (raw token), also written by the new dashboard.
//
// A token found in a cookie is copied to localStorage.__ibis_token__ so the rest of the app
// (which reads localStorage) keeps working. The copy is tagged with TOKEN_SOURCE_KEY so that a
// later dashboard login as a different user (new cookie) replaces it instead of the stale copy
// winning. Tokens from the ticket login or the legacy dashboard are never tagged, so they keep
// priority over cookies.

export const TOKEN_STORAGE_KEY = '__ibis_token__';
export const TOKEN_SOURCE_KEY = '__ibis_token_source__';
const COOKIE_SOURCE = 'cookie';

const IBIS_TOKEN_COOKIE = '__ibis_token__';
const RAW_TOKEN_COOKIE = 'ibisvision_token';
const COOKIE_DOMAIN = '.ibisvision.co.uk';

const safeStorageGet = (key) => {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
};

const safeStorageSet = (key, value) => {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // storage unavailable (private mode quota etc.) - nothing else to do
  }
};

export const getCookie = (name) => {
  const cookies = `; ${document.cookie || ''}`;
  const parts = cookies.split(`; ${name}=`);
  if (parts.length < 2) return null;
  const value = parts.pop().split(';').shift();
  return value === '' ? null : value;
};

const safeDecode = (value) => {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

// Accepts {"token": "..."} JSON (raw or URL-encoded) or a bare token string.
const parseTokenValue = (value) => {
  if (!value) return null;
  const decoded = safeDecode(value).trim();
  try {
    const parsed = JSON.parse(decoded);
    if (parsed && typeof parsed === 'object' && typeof parsed.token === 'string' && parsed.token) {
      return { token: parsed.token };
    }
    if (typeof parsed === 'string' && parsed) return { token: parsed };
    return null;
  } catch {
    // not JSON - treat as a bare token (no spaces or quotes allowed)
    return /^[^\s"{}]+$/.test(decoded) ? { token: decoded } : null;
  }
};

const readStoredToken = () => parseTokenValue(safeStorageGet(TOKEN_STORAGE_KEY));

const readCookieToken = () => parseTokenValue(getCookie(IBIS_TOKEN_COOKIE)) || parseTokenValue(getCookie(RAW_TOKEN_COOKIE));

// Returns {token} or null. Side effect: syncs a cookie token into localStorage.
export const resolveAuthToken = () => {
  const stored = readStoredToken();
  const storedFromCookie = safeStorageGet(TOKEN_SOURCE_KEY) === COOKIE_SOURCE;

  if (stored && !storedFromCookie) return stored;

  const fromCookie = readCookieToken();
  if (fromCookie) {
    if (!stored || stored.token !== fromCookie.token) {
      safeStorageSet(TOKEN_STORAGE_KEY, JSON.stringify(fromCookie));
      safeStorageSet(TOKEN_SOURCE_KEY, COOKIE_SOURCE);
    }
    return fromCookie;
  }

  // Previously copied from a cookie that is now gone (logged out of the dashboard):
  // fall back to the copy; if it is stale the API returns 401 and the app logs out.
  return stored;
};

const expireCookie = (name, domain) => {
  const domainPart = domain ? `; domain=${domain}` : '';
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${domainPart}; SameSite=Lax`;
};

// Expires the new-dashboard auth cookies (shared domain and host-only variants).
export const clearAuthCookies = () => {
  [IBIS_TOKEN_COOKIE, RAW_TOKEN_COOKIE].forEach((name) => {
    expireCookie(name, COOKIE_DOMAIN);
    expireCookie(name);
  });
};
